const API_URL = process.env.REACT_APP_API_URL;

export const fetchPublic = async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!response.ok) {
        const err = new Error(`API request failed: ${response.statusText}`);
        err.status = response.status;
        throw err;
    }

    return response.json();
};

export const fetchWithMessage = async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    let body = null;
    try { body = await response.json(); } catch {}

    if (!response.ok) {
        return {
            success: false,
            status: response.status,
            message: body?.message || `Request failed (${response.status})`,
        };
    }

    return body ?? { success: true };
};

export const getMaintenanceStatus = async () => {
    return fetchPublic('settings/maintenance');
};

export const getCurrentUser = async () => {
    return fetchPublic('auth/me');
};

export const logout = async () => {
    return fetchPublic('auth/logout', {
        method: 'POST'
    });
};

export const refreshToken = async () => {
    return fetchPublic('auth/refresh', {
        method: 'POST'
    });
};

export const toggleMaintenance = async () => {
    return fetchPublic('admin/maintenance/toggle', {
        method: 'POST'
    });
};

export const getFeatureFlags = async () => {
    return fetchPublic('admin/features');
};

export const setFeatureFlag = async (feature, enabled) => {
    return fetchPublic('admin/features', {
        method: 'POST',
        body: JSON.stringify({ feature, enabled })
    });
};

export const getActiveSessions = async () => {
    return fetchPublic('admin/sessions');
};

export const killSession = async (tokenId) => {
    return fetchPublic(`admin/sessions/${tokenId}`, {
        method: 'DELETE'
    });
};

export const killAllUserSessions = async (discordId) => {
    return fetchPublic(`admin/sessions/user/${discordId}`, {
        method: 'DELETE'
    });
};

export const cleanExpiredSessions = async () => {
    return fetchPublic('admin/sessions/clean', {
        method: 'POST'
    });
};

export const getActivePoll = async () => {
    return fetchPublic('polls/active');
};

export const votePoll = async (pollId, optionIds) => {
    return fetchPublic('polls/vote', {
        method: 'POST',
        body: JSON.stringify({ pollId, optionIds })
    });
};

export const createPoll = async (pollData) => {
    return fetchPublic('polls/create', {
        method: 'POST',
        body: JSON.stringify(pollData)
    });
};

export const getAllPolls = async () => {
    try {
        const response = await fetch(`${API_URL}polls/all`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        return response.json();
    } catch (error) {
        console.error('getAllPolls fetch error:', error);
        throw error;
    }
};

export const deletePoll = async (pollId) => {
    return fetchPublic(`polls/${pollId}`, {
        method: 'DELETE'
    });
};

export const getUserMessages = async (includeDeleted = false) => {
    const response = await fetch(`${API_URL}/messages?includeDeleted=${includeDeleted}`, {
        credentials: 'include',
    });
    return response.json();
};

export const markMessageAsRead = async (messageId) => {
    const response = await fetch(`${API_URL}/messages/${messageId}/read`, {
        method: 'POST',
        credentials: 'include',
    });
    return response.json();
};

export const deleteMessage = async (messageId) => {
    const response = await fetch(`${API_URL}/messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const createMessage = async (userId, title, message, imageUrl = null) => {
    const response = await fetch(`${API_URL}/messages/create`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userId, title, message, imageUrl }),
    });
    return response.json();
};

export const getAllCategories = async () => {
    const response = await fetch(`${API_URL}/categories`, {
        credentials: 'include',
    });
    return response.json();
};

export const createCategory = async (name, iconUrl) => {
    const response = await fetch(`${API_URL}/categories/create`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, iconUrl }),
    });
    return response.json();
};

export const deleteCategory = async (categoryId) => {
    const response = await fetch(`${API_URL}/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const getAllItems = async () => {
    const response = await fetch(`${API_URL}/categories/items`, {
        credentials: 'include',
    });
    return response.json();
};

export const searchFurniItems = async (q, signal) => {
    const response = await fetch(
        `${API_URL}/categories/items/search?q=${encodeURIComponent(q)}`,
        { signal }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
};

export const getHabboFurni = async () => {
    const response = await fetch(`${API_URL}/categories/furni`, {
        credentials: 'include',
    });
    return response.json();
};

export const updateHabboFurni = async (furniId, data) => {
    const response = await fetch(`${API_URL}/categories/furni/${furniId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
    });
    return response.json();
};

export const getReleasedItems = async () => {
    const response = await fetch(`${API_URL}/categories/items/released`, {
        credentials: 'include',
    });
    return response.json();
};

export const addValue = async (furniId, min, max) => {
    const response = await fetch(`${API_URL}/categories/values/add`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ furniId, min, max }),
    });
    return response.json();
};

export const getValueHistory = async (furniId) => {
    const response = await fetch(`${API_URL}/categories/values/${furniId}`, {
        credentials: 'include',
    });
    return response.json();
};

export const deleteValue = async (valueId) => {
    const response = await fetch(`${API_URL}/categories/values/${valueId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const getValuesOverview = async ({ staleDays = 90, limit = 30, feedLimit = 20 } = {}) => {
    const params = new URLSearchParams({
        staleDays: String(staleDays),
        limit: String(limit),
        feedLimit: String(feedLimit),
    });
    const response = await fetch(`${API_URL}/categories/overview?${params}`, {
        credentials: 'include',
    });
    return response.json();
};

export const dismissValueReview = async (furniId, { days, muted, note } = {}) => {
    const response = await fetch(`${API_URL}/categories/reviews/${furniId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ days, muted, note }),
    });
    return response.json();
};

export const clearValueReview = async (furniId) => {
    const response = await fetch(`${API_URL}/categories/reviews/${furniId}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const getFurniIndexInfo = async ({ code, name } = {}) => {
    const params = new URLSearchParams();
    if (code) params.set('code', code);
    if (name) params.set('name', name);

    const response = await fetch(`${API_URL}/categories/furniindex?${params}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getBobbaValue = async ({ code, name } = {}) => {
    const params = new URLSearchParams();
    if (code) params.set('code', code);
    if (name) params.set('name', name);

    const response = await fetch(`${API_URL}/categories/bobba?${params}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getProfilePageLoad = async (username) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/page-load`, {
        credentials: 'include',
    });
    const data = await response.json();
    // Attach HTTP status so callers can distinguish 404/403 without parsing the message
    return { ...data, _httpStatus: response.status };
};

export const getProfileData = async (username, dataType) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/data/${encodeURIComponent(dataType)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const upsertOwnProfileData = async (dataType, jsonPayload) => {
    const response = await fetch(`${API_URL}/profile/data/${encodeURIComponent(dataType)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ jsonPayload }),
    });
    return response.json();
};

export const upsertOwnStatusWithHistory = async (status, historyItem) => {
    const response = await fetch(`${API_URL}/status/combined`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, historyItem }),
    });
    return response.json();
};

export const updateCurrentStatusThumbnail = (status) =>
    fetch(`${API_URL}/status/current`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
    }).then(r => r.json());

export const getOwnedProfileSkins = async () => {
    const response = await fetch(`${API_URL}/profile/skins/owned`, {
        credentials: 'include',
    });
    return response.json();
};

export const deleteOwnProfileData = async (dataType) => {
    const response = await fetch(`${API_URL}/profile/data/${encodeURIComponent(dataType)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const listProfileFriends = async (username, limit = 20, offset = 0) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));

    const res = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/friends?${qs.toString()}`, {
        credentials: 'include',
    });
    return res.json();
};

export const getTopProfileFriends = async (username) => {
    const res = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/friends/top`, {
        credentials: 'include',
    });
    return res.json();
};

export const getProfileFriendSuggestions = async () => {
    const res = await fetch(`${API_URL}/profile/friends/suggestions`, {
        credentials: 'include',
    });
    return res.json();
};

export const getMagicWardrobeProfile = async () => {
    const res = await fetch(`${API_URL}/profile/friends/magic-wardrobe`, {
        credentials: 'include',
    });
    return res.json();
};

export const listIncomingFriendRequests = async () => {
    const response = await fetch(`${API_URL}/profile/friends/requests/incoming`, {
        credentials: 'include',
    });
    return response.json();
};

export const sendProfileFriendRequest = async (username) => {
    const response = await fetch(`${API_URL}/profile/friends/request/${encodeURIComponent(username)}`, {
        method: 'POST',
        credentials: 'include',
    });
    return response.json();
};

export const respondToProfileFriendRequest = async (username, action) => {
    const response = await fetch(`${API_URL}/profile/friends/respond/${encodeURIComponent(username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action }),
    });
    return response.json();
};

export const removeProfileFriend = async (username) => {
    const response = await fetch(`${API_URL}/profile/friends/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const setTopFriendRank = async (username, topRank) => {
    const response = await fetch(`${API_URL}/profile/friends/top-rank/${encodeURIComponent(username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ topRank }),
    });
    return response.json();
};

export const getProfileRelationship = async (username) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/relationship`, {
        credentials: 'include',
    });
    return response.json();
};

export const blockProfileUser = async (username) => {
    const response = await fetch(`${API_URL}/profile/block/${encodeURIComponent(username)}`, {
        method: 'POST',
        credentials: 'include',
    });
    return response.json();
};

export const unblockProfileUser = async (username) => {
    const response = await fetch(`${API_URL}/profile/block/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const getProfileAchievements = async (username) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/achievements`, {
        credentials: 'include',
    });
    return response.json();
};

export const listProfilePosts = async (username, limit = 20, offset = 0) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/posts?limit=${limit}&offset=${offset}`, {
        credentials: 'include',
    });
    return response.json();
};

export const createProfilePost = async (content, visibility = 'public') => {
    const response = await fetch(`${API_URL}/profile/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content, visibility, tzOffset: new Date().getTimezoneOffset() }),
    });
    return response.json();
};

export const hideProfilePost = async (postId) => {
    const response = await fetch(`${API_URL}/profile/posts/${postId}/hide`, {
        method: 'PUT',
        credentials: 'include',
    });
    return response.json();
};

export const listPostComments = async (postId, limit = 50, offset = 0, visibility = null) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (visibility) qs.set('visibility', visibility);
    const response = await fetch(`${API_URL}/profile/posts/${postId}/comments?${qs.toString()}`, {
        credentials: 'include',
    });
    return response.json();
};

export const createPostComment = async (postId, content, visibility = null) => {
    const body = { content };
    if (visibility) body.visibility = visibility;
    const response = await fetch(`${API_URL}/profile/posts/${postId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    });
    return response.json();
};

export const hideProfileComment = async (commentId) => {
    const response = await fetch(`${API_URL}/profile/comments/${commentId}/hide`, {
        method: 'PUT',
        credentials: 'include',
    });
    return response.json();
};

export const createProfileEngagement = async (username, targetType, targetID, engagementType, jsonPayload = null) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/engagements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetType, targetID, engagementType, jsonPayload }),
    });
    return response.json();
};

export const listProfileEngagements = async (username, engagementType = null, limit = 50, offset = 0) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    if (engagementType) qs.set('engagementType', engagementType);

    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/engagements?${qs.toString()}`, {
        credentials: 'include',
    });
    return response.json();
};

export const deleteProfileEngagement = async (username, targetType, targetID, engagementType) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/engagements`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetType, targetID, engagementType }),
    });
    return response.json();
};


export const respondProfileFriendRequest = respondToProfileFriendRequest;

export const createProfileReport = async (username, reason, details) => {
    const response = await fetch(`${API_URL}/profile/report/${encodeURIComponent(username)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, details }),
    });
    return response.json();
};

export const listProfileReports = async (limit = 50, offset = 0, status = 'open') => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset), status: String(status || '') });
    const response = await fetch(`${API_URL}/profile/reports?${params.toString()}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getProfileReport = async (reportId) => {
    const response = await fetch(`${API_URL}/profile/reports/${encodeURIComponent(reportId)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const awardOwnProfileAchievement = async (badgeID) => {
    const response = await fetch(`${API_URL}/profile/achievements/award`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ badgeID }),
    });
    return response.json();
};
export const getTopPost = async (period) => {
    const response = await fetch(`${API_URL}/profile/posts/top?period=${encodeURIComponent(period)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getRecentPosts = async (limit = 20) => {
    const response = await fetch(`${API_URL}/profile/posts/recent?limit=${limit}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getImagerBlob = async () => {
    const response = await fetch(`${API_URL}imager/blob`, {
        credentials: 'include',
    });
    return response.json();
};

export const upsertImagerBlob = async (encoding, blob, updatedAt) => {
    const response = await fetch(`${API_URL}imager/blob`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ encoding, blob, updatedAt }),
    });
    return response.json();
};

export const sulakeLogin = async (username, password) => {
    const response = await fetch(`${API_URL}imager/sulake-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
    });
    return response.json();
};

export const getSharedImagerBlob = async (sharedKey) => {
    const response = await fetch(`${API_URL}imager/blob/shared`, {
        headers: { 'X-Sulake-Key': sharedKey },
    });
    return response.json();
};

export const upsertSharedImagerBlob = async (sharedKey, encoding, blob, updatedAt) => {
    const response = await fetch(`${API_URL}imager/blob/shared`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'X-Sulake-Key': sharedKey,
        },
        body: JSON.stringify({ encoding, blob, updatedAt }),
    });
    return response.json();
};

export const getPublicImagerScenes = async (username) => {
    const response = await fetch(`${API_URL}imager/scenes?username=${encodeURIComponent(username)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getMyImagerScenes = async () => {
    const response = await fetch(`${API_URL}imager/scenes/mine`, {
        credentials: 'include',
    });
    return response.json();
};

export const saveImagerScene = async (title, sceneJson, thumbnailUrl, isPublic) => {
    const response = await fetch(`${API_URL}imager/scenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title, sceneJson, thumbnailUrl, isPublic }),
    });
    return response.json();
};

export const updateImagerScene = async (id, updates) => {
    const response = await fetch(`${API_URL}imager/scenes/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
    });
    return response.json();
};

export const deleteImagerScene = async (id) => {
    const response = await fetch(`${API_URL}imager/scenes/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const getActiveRestrictions = async (userID) => {
    const response = await fetch(`${API_URL}/profile-reports/user/${userID}/restrictions`, {
        credentials: 'include',
    });
    return response.json();
};

export const getActiveBan = async (userID) => {
    const response = await fetch(`${API_URL}/profile-reports/user/${userID}/ban`, {
        credentials: 'include',
    });
    return response.json();
};

export const countMyGivenEngagements = async (engagementType, targetType) => {
    const qs = new URLSearchParams();
    qs.set('engagementType', engagementType);
    qs.set('targetType', targetType);

    const response = await fetch(`${API_URL}/profile/engagements/given-count?${qs.toString()}`, {
        credentials: 'include',
    });
    return response.json();
};

export const listMyGivenEngagementsRecent = async (engagementType, targetType, limit = 3) => {
    const qs = new URLSearchParams();
    qs.set('engagementType', engagementType);
    qs.set('targetType', targetType);
    qs.set('limit', String(limit));

    const res = await fetch(`${API_URL}/profile/engagements/given-recent?${qs.toString()}`, {
        credentials: 'include',
    });
    return res.json();
};

export const getProfileEngagementSummary = async (username, engagementType, targetType, targetID = null) => {
    const qs = new URLSearchParams();
    qs.set('engagementType', engagementType);
    qs.set('targetType', targetType);
    if (targetID != null) qs.set('targetID', String(targetID));

    const res = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/engagements/summary?${qs.toString()}`, {
        credentials: 'include',
    });
    return res.json();
};

export const getBulkEngagementCounts = async (engagementType, targetType, targetIDs) => {
    const res = await fetch(`${API_URL}/profile/engagements/bulk-counts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ engagementType, targetType, targetIDs }),
    });
    return res.json();
};

export const getTopPostsFeed = async (feed, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_URL}/top-posts/posts/${feed}${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
    });
    return response.json();
};

export const likeTopPost = async (postId) => {
    const response = await fetch(`${API_URL}/top-posts/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include',
    });
    return response.json();
};

export const unlikeTopPost = async (postId) => {
    const response = await fetch(`${API_URL}/top-posts/posts/${postId}/like`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response.json();
};

export const giveTopPostsLuv = async (username) => {
    const response = await fetch(`${API_URL}/profile/${encodeURIComponent(username)}/engagements`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetType: 'profile', targetID: null, engagementType: 'luv' }),
    });
    return response.json();
};

export const getTopPostsCommunityStats = async () => {
    const response = await fetch(`${API_URL}/top-posts/stats`, {
        credentials: 'include',
    });
    return response.json();
};

export const getBannerOffsets = async () => {
    return fetchPublic('admin/banner-offsets');
};

export const getFurniBillboard = async () => {
    return fetchPublic('furni/billboard');
};

export const upsertBannerOffset = async (payload) => {
    return fetchPublic('admin/banner-offsets', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

export const deleteBannerOffset = async (furniName) => {
    return fetchPublic(`admin/banner-offsets/${encodeURIComponent(furniName)}`, {
        method: 'DELETE',
    });
};

export const searchUsers = async (q, signal) => {
    const response = await fetch(`${API_URL.replace(/\/$/, '')}/search?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
        signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
};

export const getCircleBgCatalogue = async () => {
    const response = await fetch(`${API_URL}/circle-bg/catalogue`, {
        credentials: 'include',
    });
    return response.json();
};

export const getAdminCircleBgs = async () => fetchPublic('circlebgs/admin');

export const saveCircleBg = async ({ bgId, name, file, isNew }) => {
    const form = new FormData();
    form.append('bgId', bgId);
    form.append('name', name);
    if (file) form.append('image', file);

    const url = isNew
        ? `${API_URL}circlebgs/admin`
        : `${API_URL}circlebgs/admin/${encodeURIComponent(bgId)}`;

    const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        credentials: 'include',
        body: form,
    });

    let body = null;
    try { body = await response.json(); } catch {}

    if (!response.ok) {
        return { success: false, message: body?.message || `Save failed (${response.status})` };
    }
    return body ?? { success: false, message: 'Save failed' };
};

export const deleteCircleBg = async (bgId) =>
    fetchWithMessage(`circlebgs/admin/${encodeURIComponent(bgId)}`, { method: 'DELETE' });

export const getMyCircleBgs = async () => {
    const response = await fetch(`${API_URL}/circle-bg/mine`, {
        credentials: 'include',
    });
    return response.json();
};

export const getAllAchievements = async () => {
    const response = await fetch(`${API_URL}/achievements/all`, { credentials: 'include' });
    return response.json();
};

export const awardAchievement = async (habboName, badgeID) => {
    const response = await fetch(`${API_URL}/achievements/award`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ habboName, badgeID }),
    });
    return response.json();
};

export const getUserAchievements = async (habboName) => {
    const response = await fetch(`${API_URL}/achievements/user/${encodeURIComponent(habboName)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const removeAchievement = async (habboName, badgeID) => {
    const response = await fetch(`${API_URL}/achievements/remove`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ habboName, badgeID }),
    });
    return response.json();
};

export const registerAchievement = async (badgeID, name, description, category, birthdayDate, birthdayUser) => {
    const response = await fetch(`${API_URL}/achievements/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ badgeID, name, description, category, birthdayDate, birthdayUser }),
    });
    return response.json();
};

export const updateAchievement = async (badgeID, updates) => {
    const response = await fetch(`${API_URL}/achievements/${encodeURIComponent(badgeID)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
    });
    return response.json();
};

export const uploadAchievement = async (file, name, description, category, birthdayDate, birthdayUser) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('name', name);
    formData.append('description', description);
    if (category) formData.append('category', category);
    if (birthdayDate) formData.append('birthdayDate', birthdayDate);
    if (birthdayUser) formData.append('birthdayUser', birthdayUser);
    const response = await fetch(`${API_URL}/achievements/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    });
    return response.json();
};

export const checkUserRoles = async (habboName) => {
    const response = await fetch(`${API_URL}/achievements/check/${encodeURIComponent(habboName)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const getArticlesByCategory = async (categorySlug, page = 1) => {
    return fetchPublic(`articles/filter/${categorySlug}?page=${page}`);
};

export const getPublicCategories = async () => {
    return fetchPublic('articles/categories/public');
};

export const searchArticles = async ({ q = '', startDate = '', endDate = '', categorySlug = '', page = 1 } = {}) => {
    const params = new URLSearchParams();

    if (q) params.set('q', q);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (categorySlug) params.set('categorySlug', categorySlug);
    params.set('page', String(page));

    return fetchPublic(`articles/search?${params.toString()}`);
};

// ── Games ──────────────────────────────────────────────────────────────────────

export const logGameScore = (score, outcome) =>
    fetchPublic('games/score', { method: 'POST', body: JSON.stringify({ score, outcome }) });

export const canSubmitGameScore = () =>
    fetchPublic('games/can-submit');

export const submitGameScore = (scoreId) =>
    fetchPublic(`games/submit/${scoreId}`, { method: 'POST' });

export const claimFranklyStamp = () =>
    fetchPublic('games/frankly-stamp', { method: 'POST' });

export const getGameLeaderboard = (mode = 'winners') =>
    fetchPublic(`games/leaderboard?mode=${mode}`);

export const getLatestGameSubmissions = () =>
    fetchPublic('games/submissions');

// ── Slimeburger Spin ────────────────────────────────────────────────────────────────

const fetchSlot = async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...options.headers },
    });

    let body = null;
    try { body = await response.json(); } catch { body = null; }

    if (body && typeof body === 'object') return body;

    const err = new Error(`API request failed: ${response.statusText}`);
    err.status = response.status;
    throw err;
};

export const getSlotPaytable = () =>
    fetchSlot('slots/paytable');

export const getSlotState = () =>
    fetchSlot('slots/state');

export const startSlotDaily = () =>
    fetchSlot('slots/daily/start', { method: 'POST' });

export const slotSpin = (mode, betCards, force, forceCount) =>
    fetchSlot('slots/spin', { method: 'POST', body: JSON.stringify({ mode, betCards, force, forceCount }) });

export const slotBonusStep = (bonusId) =>
    fetchSlot('slots/bonus/step', { method: 'POST', body: JSON.stringify({ bonusId }) });

export const slotAck = (spinId) =>
    fetchSlot('slots/ack', { method: 'POST', body: JSON.stringify({ spinId }) });

export const claimSlotDaily = () =>
    fetchSlot('slots/daily/claim', { method: 'POST' });

export const endSlotStakes = () =>
    fetchSlot('slots/stakes/end', { method: 'POST' });

export const getSlotLeaderboard = (period = 'alltime') =>
    fetchSlot(`slots/leaderboard?period=${period}`);

export const getSlotSubmissions = () =>
    fetchSlot('slots/submissions');

export const getSlotWheel = () =>
    fetchSlot('slots/wheel');

export const spinSlotWheel = () =>
    fetchSlot('slots/wheel/spin', { method: 'POST' });

export const getSlotAdminOverview = () =>
    fetchSlot('slots/admin/overview');

export const getSlotAdminPlayer = (habboName) =>
    fetchSlot(`slots/admin/player?habboName=${encodeURIComponent(habboName)}`);

export const getSlotAdminSettings = () =>
    fetchSlot('slots/admin/settings');

export const updateSlotAdminSettings = (settings) =>
    fetchSlot('slots/admin/settings', { method: 'PUT', body: JSON.stringify({ settings }) });

export const getSlotWheelItems = () =>
    fetchSlot('slots/admin/wheel/items');

export const createSlotWheelItem = (item) =>
    fetchSlot('slots/admin/wheel/items', { method: 'POST', body: JSON.stringify(item) });

export const updateSlotWheelItem = (itemId, item) =>
    fetchSlot(`slots/admin/wheel/items/${itemId}`, { method: 'PUT', body: JSON.stringify(item) });

export const deleteSlotWheelItem = (itemId) =>
    fetchSlot(`slots/admin/wheel/items/${itemId}`, { method: 'DELETE' });

export const getSlotWheelDeliveries = (state) =>
    fetchSlot(`slots/admin/wheel/deliveries${state ? `?state=${encodeURIComponent(state)}` : ''}`);

export const updateSlotWheelDelivery = (deliveryId, body) =>
    fetchSlot(`slots/admin/wheel/deliveries/${deliveryId}`, { method: 'PUT', body: JSON.stringify(body) });

export const getSlotWheelLog = (limit = 50) =>
    fetchSlot(`slots/admin/wheel/log?limit=${limit}`);

export const grantSlotWheelSpin = (habboNames) =>
    fetchSlot('slots/admin/wheel/grant', { method: 'POST', body: JSON.stringify({ habboNames }) });

export const getSlotGifts = (limit = 50) =>
    fetchSlot(`slots/admin/gifts?limit=${limit}`);

export const createSlotGift = (gift) =>
    fetchSlot('slots/admin/gifts', { method: 'POST', body: JSON.stringify(gift) });

export const revokeSlotGift = (giftId) =>
    fetchSlot(`slots/admin/gifts/${giftId}`, { method: 'DELETE' });

// ── Status ────────────────────────────────────────────────────────────────────

export const getFriendsStatuses = (limit = 15, offset = 0) =>
    fetchPublic(`status/friends?limit=${limit}&offset=${offset}`);

export const getCommunityStatuses = (limit = 48, offset = 0) =>
    fetchPublic(`status/community?limit=${limit}&offset=${offset}`);

export const getStatusHistoryItems = (username) =>
    fetchPublic(`profile/${encodeURIComponent(username)}/data/status_history_items`);

export const deleteStatusHistoryItemRow = (rowId) =>
    fetchPublic(`status/history/${rowId}`, { method: 'DELETE' });

export const updateStatusHistoryItemRow = (rowId, payload) =>
    fetchPublic(`status/history/${rowId}`, {
        method: 'PUT',
        body: JSON.stringify({ jsonPayload: payload }),
    });

export const searchStatusMusic   = (q) => fetchPublic(`status/search/music?q=${encodeURIComponent(q)}`);
export const searchStatusMovies  = (q) => fetchPublic(`status/search/movies?q=${encodeURIComponent(q)}`);
export const searchStatusGames   = (q) => fetchPublic(`status/search/games?q=${encodeURIComponent(q)}`);
export const searchStatusBooks   = (q) => fetchPublic(`status/search/books?q=${encodeURIComponent(q)}`);
export const searchStatusPodcasts = (q) => fetchPublic(`status/search/podcasts?q=${encodeURIComponent(q)}`);

export const getEasterBunnyStatus = async (userId) => {
    const response = await fetch(`${API_URL}/easter-bunny-status?userId=${encodeURIComponent(userId)}`, {
        credentials: 'include',
    });
    return response.json();
};

export const rollEasterPrize = async (userId) => {
    const response = await fetch(`${API_URL}/easter-roll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
    });
    return response.json();
};

export const claimEasterPrize = async (userId) => {
    const response = await fetch(`${API_URL}/easter-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
    });
    return response.json();
};

export const getFranklyCloudItems = async () => {
    const response = await fetch(`${API_URL}franklycloud/items`, { credentials: 'include' });
    return response.json();
};

export const uploadFranklyCloudImage = (file, onProgress) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}franklycloud/upload`);
    xhr.withCredentials = true;

    if (typeof onProgress === 'function') {
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
    }

    xhr.onload = () => {
        try {
            resolve(JSON.parse(xhr.responseText));
        } catch {
            resolve({ success: false, message: 'Unexpected response from the server.' });
        }
    };
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onabort = () => reject(new Error('Upload aborted.'));

    xhr.send(formData);
});

export const addFranklyCloudProImager = async ({ name, encoding, blob }) => {
    const response = await fetch(`${API_URL}franklycloud/proimager`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, encoding, blob }),
    });
    return response.json();
};

export const deleteFranklyCloudItem = async (id) => {
    const response = await fetch(`${API_URL}franklycloud/item/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
    return response.json();
};

export const getFranklyCloudItemsForUser = async (username) => {
    const response = await fetch(`${API_URL}franklycloud/public/${encodeURIComponent(username)}`, { credentials: 'include' });
    return response.json();
};

export const setCloudUploadMode = async (mode) => {
    return fetchPublic('admin/cloud-upload-mode', {
        method: 'POST',
        body: JSON.stringify({ mode }),
    });
};

export const getAdminCloudImages = async (page = 1) => {
    return fetchPublic(`admin/cloud-images?page=${page}`);
};

export const adminDeleteCloudImage = async (id) => {
    return fetchPublic(`admin/cloud-images/${encodeURIComponent(id)}`, { method: 'DELETE' });
};

export const getPartners = async () => fetchPublic('admin/partners');

export const createPartner = async ({ name, origins, dailyQuota }) =>
    fetchPublic('admin/partners', {
        method: 'POST',
        body: JSON.stringify({ name, origins, dailyQuota }),
    });

export const getPartnerIntegration = async (id) =>
    fetchPublic(`admin/partners/${encodeURIComponent(id)}/integration`);

export const updatePartner = async (id, changes) =>
    fetchPublic(`admin/partners/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
    });

export const regeneratePartnerKey = async (id) =>
    fetchPublic(`admin/partners/${encodeURIComponent(id)}/regenerate`, { method: 'POST' });

export const createPartnerPreviewToken = async (id) =>
    fetchPublic(`admin/partners/${encodeURIComponent(id)}/preview-token`, { method: 'POST' });

export const deletePartner = async (id) =>
    fetchPublic(`admin/partners/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const getPartnerUploads = async (page = 1, partnerId = null) =>
    fetchPublic(`admin/partners/uploads?page=${page}${partnerId ? `&partnerId=${encodeURIComponent(partnerId)}` : ''}`);

export const getProfileSkins = async () => fetchPublic('profileskins');

export const getAdminProfileSkins = async () => fetchPublic('profileskins/admin');

export const createProfileSkin = async (skin) =>
    fetchWithMessage('profileskins/admin', {
        method: 'POST',
        body: JSON.stringify(skin),
    });

export const updateProfileSkin = async (id, skin) =>
    fetchWithMessage(`profileskins/admin/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(skin),
    });

export const deleteProfileSkin = async (id) =>
    fetchWithMessage(`profileskins/admin/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });

export const listSkinAssets = async () => fetchPublic('profileskins/admin/assets/list');

export const uploadSkinAsset = async (file) => {
    const form = new FormData();
    form.append('image', file);

    const response = await fetch(`${API_URL}profileskins/admin/assets`, {
        method: 'POST',
        credentials: 'include',
        body: form,
    });

    let body = null;
    try { body = await response.json(); } catch {}

    if (!response.ok) {
        return { success: false, message: body?.message || `Upload failed (${response.status})` };
    }

    return body ?? { success: false, message: 'Upload failed' };
};

export const getActiveSiteTheme = async () => fetchPublic('themes/active');

export const getAdminSiteThemes = async () => fetchPublic('themes/admin');

export const createSiteTheme = async (theme) =>
    fetchWithMessage('themes/admin', {
        method: 'POST',
        body: JSON.stringify(theme),
    });

export const updateSiteTheme = async (id, theme) =>
    fetchWithMessage(`themes/admin/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(theme),
    });

export const deleteSiteTheme = async (id) =>
    fetchWithMessage(`themes/admin/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });

export const activateSiteTheme = async (key) =>
    fetchWithMessage('themes/admin/activate', {
        method: 'POST',
        body: JSON.stringify({ key }),
    });

export const listSiteThemeAssets = async () => fetchPublic('themes/admin/assets/list');

export const deleteSiteThemeAsset = async (filename) =>
    fetchWithMessage(`themes/admin/assets/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
    });

export const uploadSiteThemeAsset = async (file) => {
    const form = new FormData();
    form.append('image', file);

    const response = await fetch(`${API_URL}themes/admin/assets`, {
        method: 'POST',
        credentials: 'include',
        body: form,
    });

    let body = null;
    try { body = await response.json(); } catch {}

    if (!response.ok) {
        return { success: false, message: body?.message || `Upload failed (${response.status})` };
    }

    return body ?? { success: false, message: 'Upload failed' };
};

export const getStaffTeams = async () => fetchPublic('staff/teams');

export const uploadStaffImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_URL}upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    });

    let payload = null;
    try { payload = await response.json(); } catch { payload = null; }

    if (!response.ok || !payload?.url) {
        return { success: false, message: payload?.message || 'That image would not upload' };
    }

    return { success: true, url: payload.url, filename: payload.filename };
};

export const getFranklyMailAudience = async ({ scope, habboName = '', joinedSince = '' }) => {
    const params = new URLSearchParams({ scope });
    if (habboName) params.set('habboName', habboName);
    if (joinedSince) params.set('joinedSince', joinedSince);
    return fetchSoft(`staffmail/audience?${params.toString()}`);
};

export const sendFranklyMail = async (payload) =>
    fetchSoft('staffmail/send', { method: 'POST', body: JSON.stringify(payload) });

export const getStaffMail = async (state = 'open') =>
    fetchSoft(`staffmail?state=${encodeURIComponent(state)}`);

export const getStaffMailCount = async () => fetchSoft('staffmail/count');

export const markStaffMailSeen = async (messageId) =>
    fetchSoft(`staffmail/${encodeURIComponent(messageId)}/seen`, { method: 'POST' });

export const dismissStaffMail = async (messageId, note = '') =>
    fetchSoft(`staffmail/${encodeURIComponent(messageId)}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({ note }),
    });

export const postStaffMail = async (payload) =>
    fetchSoft('staffmail', { method: 'POST', body: JSON.stringify(payload) });

export const getStaffLog = async ({ limit = 40, beforeId = null, afterId = null, category = '' } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (beforeId) params.set('beforeId', String(beforeId));
    if (afterId) params.set('afterId', String(afterId));
    if (category) params.set('category', category);
    return fetchPublic(`staff/log?${params.toString()}`);
};

export const getStaffCalendar = async ({ from, to }) => {
    const params = new URLSearchParams({ from, to });
    return fetchWithMessage(`staffcalendar?${params.toString()}`);
};

export const createStaffCalendarEntry = async (entry) =>
    fetchWithMessage('staffcalendar', {
        method: 'POST',
        body: JSON.stringify(entry),
    });

export const updateStaffCalendarEntry = async (entryId, entry) =>
    fetchWithMessage(`staffcalendar/${encodeURIComponent(entryId)}`, {
        method: 'PUT',
        body: JSON.stringify(entry),
    });

export const deleteStaffCalendarEntry = async (entryId) =>
    fetchWithMessage(`staffcalendar/${encodeURIComponent(entryId)}`, {
        method: 'DELETE',
    });

const eventQuery = ({ from, to, host, roomOwner, addedBy, type } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (host) params.set('host', host);
    if (roomOwner) params.set('roomOwner', roomOwner);
    if (addedBy) params.set('addedBy', addedBy);
    if (type) params.set('type', type);

    const query = params.toString();
    return query ? `?${query}` : '';
};

export const getStaffEvents = async (filters) =>
    fetchWithMessage(`events${eventQuery(filters)}`);

export const getStaffEventStats = async (filters) =>
    fetchWithMessage(`events/stats${eventQuery(filters)}`);

export const getStaffEventPeople = async () => fetchWithMessage('events/people');

export const createStaffEvent = async (event) =>
    fetchWithMessage('events', {
        method: 'POST',
        body: JSON.stringify(event),
    });

export const updateStaffEvent = async (eventId, event) =>
    fetchWithMessage(`events/${encodeURIComponent(eventId)}`, {
        method: 'PUT',
        body: JSON.stringify(event),
    });

export const deleteStaffEvent = async (eventId) =>
    fetchWithMessage(`events/${encodeURIComponent(eventId)}`, {
        method: 'DELETE',
    });

export const getAdminStaffTeams = async () => fetchPublic('staff/admin/teams');

export const createStaffTeam = async (team) =>
    fetchPublic('staff/admin/teams', {
        method: 'POST',
        body: JSON.stringify(team),
    });

export const updateStaffTeam = async (teamId, team) =>
    fetchPublic(`staff/admin/teams/${encodeURIComponent(teamId)}`, {
        method: 'PUT',
        body: JSON.stringify(team),
    });

export const deleteStaffTeam = async (teamId) =>
    fetchPublic(`staff/admin/teams/${encodeURIComponent(teamId)}`, { method: 'DELETE' });

export const getAdminStaffMembers = async () => fetchPublic('staff/admin/members');

export const createStaffMember = async (member) =>
    fetchPublic('staff/admin/members', {
        method: 'POST',
        body: JSON.stringify(member),
    });

export const updateStaffMember = async (memberId, member) =>
    fetchPublic(`staff/admin/members/${encodeURIComponent(memberId)}`, {
        method: 'PUT',
        body: JSON.stringify(member),
    });

export const deleteStaffMember = async (memberId) =>
    fetchPublic(`staff/admin/members/${encodeURIComponent(memberId)}`, { method: 'DELETE' });

export const searchLookupUsers = async (term) =>
    fetchPublic(`admin/user-lookup/search?q=${encodeURIComponent(term)}`);

export const getLookupUserOverview = async (identifier) =>
    fetchPublic(`admin/user-lookup/${encodeURIComponent(identifier)}`);

export const getGdprErasurePreview = async (userId) =>
    fetchWithMessage(`admin/gdpr/${encodeURIComponent(userId)}`);

export const eraseUserForGdpr = async (userId, confirmHabboName) =>
    fetchWithMessage(`admin/gdpr/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmHabboName }),
    });

const fetchSoft = async (endpoint, options = {}) => {
    const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    let data;
    try {
        data = await response.json();
    } catch {
        data = {};
    }

    return { ...data, success: data.success === true, status: response.status };
};

export const getAppreciationWall = async (page = 1, sort = 'latest') =>
    fetchSoft(`appreciation/wall?page=${page}&sort=${encodeURIComponent(sort)}`);

export const getAppreciationConfig = async () => fetchSoft('appreciation/config');

export const checkAppreciationHabbo = async (habboName) =>
    fetchSoft('appreciation/check-habbo', {
        method: 'POST',
        body: JSON.stringify({ habboName }),
    });

export const createAppreciationNomination = async ({ habboName, message, anonymous }) =>
    fetchSoft('appreciation/nominate', {
        method: 'POST',
        body: JSON.stringify({ habboName, message, anonymous }),
    });

export const likeAppreciationPost = async (postId) =>
    fetchSoft(`appreciation/posts/${encodeURIComponent(postId)}/like`, { method: 'POST' });

export const unlikeAppreciationPost = async (postId) =>
    fetchSoft(`appreciation/posts/${encodeURIComponent(postId)}/like`, { method: 'DELETE' });

export const getAppreciationStaffSettings = async () => fetchSoft('appreciation/staff/settings');

export const updateAppreciationStaffSettings = async (settings) =>
    fetchSoft('appreciation/staff/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
    });

export const getAppreciationStaffPosts = async ({ page = 1, from = '', to = '', includeRemoved = false } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (includeRemoved) params.set('includeRemoved', '1');
    return fetchSoft(`appreciation/staff/posts?${params.toString()}`);
};

export const getAppreciationStaffStats = async ({ from = '', to = '' } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchSoft(`appreciation/staff/stats${qs ? `?${qs}` : ''}`);
};

export const removeAppreciationStaffPost = async (postId) =>
    fetchSoft(`appreciation/staff/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });

export const askFrank = async ({ q, page = 1, variant = 0 } = {}) => {
    const params = new URLSearchParams({ q: String(q || ''), page: String(page) });
    if (variant) params.set('variant', String(variant));
    return fetchSoft(`askfrank/ask?${params.toString()}`);
};

export const getAskFrankSuggestions = async (q) =>
    fetchSoft(`askfrank/suggest?q=${encodeURIComponent(q || '')}`);

export const getAskFrankTrending = async () => fetchSoft('askfrank/trending');

export const getFranklyNetLinks = async () => fetchSoft('askfrank/links');

export const resolveFranklyNetTarget = async (url) =>
    fetchSoft(`askfrank/resolve?url=${encodeURIComponent(url || '')}`);

export const getAskFrankStatus = async () => fetchSoft('askfrank/status');

export const submitAskFrankSource = async (payload) =>
    fetchSoft('askfrank/submit', { method: 'POST', body: JSON.stringify(payload) });

export const getAskFrankSources = async (status = '') =>
    fetchSoft(`askfrank/admin/sources${status ? `?status=${encodeURIComponent(status)}` : ''}`);

export const createAskFrankSource = async (payload) =>
    fetchSoft('askfrank/admin/sources', { method: 'POST', body: JSON.stringify(payload) });

export const updateAskFrankSource = async (sourceId, payload) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankSource = async (sourceId) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}`, { method: 'DELETE' });

export const checkAskFrankRobots = async (sourceId) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}/robots`, { method: 'POST' });

export const requeueAskFrankSource = async (sourceId) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}/requeue`, { method: 'POST' });

export const getAskFrankSourcePages = async (sourceId, page = 1) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}/pages?page=${page}`);

export const recrawlAskFrankPage = async (pageId) =>
    fetchSoft(`askfrank/admin/pages/${encodeURIComponent(pageId)}/recrawl`, { method: 'POST' });

export const deleteAskFrankPage = async (pageId) =>
    fetchSoft(`askfrank/admin/pages/${encodeURIComponent(pageId)}`, { method: 'DELETE' });

export const getAskFrankLinks = async () => fetchSoft('askfrank/admin/links');

export const saveAskFrankLink = async (linkId, payload) =>
    fetchSoft(`askfrank/admin/links${linkId ? `/${encodeURIComponent(linkId)}` : ''}`, {
        method: linkId ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankLink = async (linkId) =>
    fetchSoft(`askfrank/admin/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });

export const getAskFrankPhrases = async () => fetchSoft('askfrank/admin/phrases');

export const saveAskFrankPhrase = async (phraseId, payload) =>
    fetchSoft(`askfrank/admin/phrases${phraseId ? `/${encodeURIComponent(phraseId)}` : ''}`, {
        method: phraseId ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankPhrase = async (phraseId) =>
    fetchSoft(`askfrank/admin/phrases/${encodeURIComponent(phraseId)}`, { method: 'DELETE' });

export const getAskFrankSynonyms = async () => fetchSoft('askfrank/admin/synonyms');

export const saveAskFrankSynonym = async (synonymId, payload) =>
    fetchSoft(`askfrank/admin/synonyms${synonymId ? `/${encodeURIComponent(synonymId)}` : ''}`, {
        method: synonymId ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankSynonym = async (synonymId) =>
    fetchSoft(`askfrank/admin/synonyms/${encodeURIComponent(synonymId)}`, { method: 'DELETE' });

export const getAskFrankRules = async () => fetchSoft('askfrank/admin/rules');

export const saveAskFrankRule = async (payload) =>
    fetchSoft('askfrank/admin/rules', { method: 'POST', body: JSON.stringify(payload) });

export const deleteAskFrankRule = async (ruleId) =>
    fetchSoft(`askfrank/admin/rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' });

export const getAskFrankInsights = async () => fetchSoft('askfrank/admin/insights');

export const getFranklyNetArticle = async (slug) =>
    fetchSoft(`askfrank/article/${encodeURIComponent(slug)}`);

export const getFranklyNetEntity = async (slug) =>
    fetchSoft(`askfrank/entity/${encodeURIComponent(slug)}`);

export const getAskFrankEntities = async ({ kind = '', status = '', q = '', page = 1 } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    if (kind) params.set('kind', kind);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    return fetchSoft(`askfrank/admin/entities?${params.toString()}`);
};

export const getAskFrankEntity = async (entityId) =>
    fetchSoft(`askfrank/admin/entities/${encodeURIComponent(entityId)}`);

export const saveAskFrankEntity = async (entityId, payload) =>
    fetchSoft(`askfrank/admin/entities${entityId ? `/${encodeURIComponent(entityId)}` : ''}`, {
        method: entityId ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankEntity = async (entityId) =>
    fetchSoft(`askfrank/admin/entities/${encodeURIComponent(entityId)}`, { method: 'DELETE' });

export const getAskFrankCandidates = async (status = 'new') =>
    fetchSoft(`askfrank/admin/candidates?status=${encodeURIComponent(status)}`);

export const promoteAskFrankCandidate = async (candidateId, payload = {}) =>
    fetchSoft(`askfrank/admin/candidates/${encodeURIComponent(candidateId)}/promote`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });

export const dismissAskFrankCandidate = async (candidateId) =>
    fetchSoft(`askfrank/admin/candidates/${encodeURIComponent(candidateId)}/dismiss`, { method: 'POST' });

export const listAskFrankEntityImages = async () => fetchSoft('askfrank/admin/entities/images');

export const uploadAskFrankEntityImage = async (file) => {
    const form = new FormData();
    form.append('image', file);

    const response = await fetch(`${API_URL}askfrank/admin/entities/image`, {
        method: 'POST',
        credentials: 'include',
        body: form,
    });

    let body = null;
    try { body = await response.json(); } catch {}

    if (!response.ok) {
        return { success: false, message: body?.message || `Upload failed (${response.status})` };
    }

    return body ?? { success: false, message: 'Upload failed' };
};

export const getAskFrankKeywords = async () => fetchSoft('askfrank/admin/keywords');

export const saveAskFrankKeyword = async (keywordId, payload) =>
    fetchSoft(`askfrank/admin/keywords${keywordId ? `/${encodeURIComponent(keywordId)}` : ''}`, {
        method: keywordId ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
    });

export const deleteAskFrankKeyword = async (keywordId) =>
    fetchSoft(`askfrank/admin/keywords/${encodeURIComponent(keywordId)}`, { method: 'DELETE' });

export const runAskFrankEntityMiner = async (mode = 'full') =>
    fetchSoft('askfrank/admin/entities/mine', {
        method: 'POST',
        body: JSON.stringify({ mode }),
    });

export const repairAskFrankNewsUrls = async () =>
    fetchSoft('askfrank/admin/news/repair-urls', { method: 'POST' });

export const reindexFranklyContent = async () =>
    fetchSoft('askfrank/admin/frankly/reindex', { method: 'POST' });

export const getLatestHabboArticles = async (limit = 8) =>
    fetchSoft(`askfrank/articles/latest?limit=${limit}`);

export const getAskFrankNewsStatus = async () => fetchSoft('askfrank/admin/news');

export const backfillAskFrankNews = async (directory) =>
    fetchSoft('askfrank/admin/news/backfill', {
        method: 'POST',
        body: JSON.stringify({ directory }),
    });

export const syncAskFrankNews = async (pages = 2) =>
    fetchSoft('askfrank/admin/news/sync', {
        method: 'POST',
        body: JSON.stringify({ pages }),
    });

export const resolveAskFrankNewsIds = async (pages = 80) =>
    fetchSoft('askfrank/admin/news/resolve-ids', {
        method: 'POST',
        body: JSON.stringify({ pages }),
    });

export const recordAskFrankClick = async (pageId) =>
    fetchSoft('askfrank/click', {
        method: 'POST',
        body: JSON.stringify({ pageId }),
    });

export const reindexAskFrankNews = async () =>
    fetchSoft('askfrank/admin/news/reindex', { method: 'POST' });

export const getAskFrankBrain = async () => fetchSoft('askfrank/admin/brain');

export const getAskFrankLexicon = async ({ q = '', kind = '', page = 1 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (kind) params.set('kind', kind);
    params.set('page', String(page));
    return fetchSoft(`askfrank/admin/brain/lexicon?${params.toString()}`);
};

export const updateAskFrankLexiconTerm = async (termId, payload) =>
    fetchSoft(`askfrank/admin/brain/lexicon/${encodeURIComponent(termId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });

export const rebuildAskFrankLexicon = async (skipAssoc = false) =>
    fetchSoft('askfrank/admin/brain/lexicon/rebuild', {
        method: 'POST',
        body: JSON.stringify({ skipAssoc }),
    });

export const rebuildAskFrankAssociations = async () =>
    fetchSoft('askfrank/admin/brain/associations/rebuild', { method: 'POST' });

export const getAskFrankAssociations = async (term) =>
    fetchSoft(`askfrank/admin/brain/associations?term=${encodeURIComponent(term || '')}`);

export const backfillAskFrankEmbeddings = async (limit = 300) =>
    fetchSoft('askfrank/admin/brain/embeddings/backfill', {
        method: 'POST',
        body: JSON.stringify({ limit }),
    });

export const explainAskFrankQuery = async (q) =>
    fetchSoft(`askfrank/admin/brain/explain?q=${encodeURIComponent(q || '')}`);

export const getAskFrankUnanswered = async () => fetchSoft('askfrank/admin/brain/unanswered');

export const diagnoseAskFrankSource = async (sourceId) =>
    fetchSoft(`askfrank/admin/sources/${encodeURIComponent(sourceId)}/diagnose`);

export const reportAskFrankResults = async (payload) =>
    fetchSoft('askfrank/report', { method: 'POST', body: JSON.stringify(payload) });
